import React, {useState, useEffect} from 'react'
import CustomModal from 'app/components/CustomModal'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import cloudService from '../../services/cloud.services'
import {useAuth} from 'app/modules/auth'
import Input from 'app/components/Input'
import Button from 'app/components/Button'

interface CloudData {
  id: string
  type: string
  purpose: string
  name: string
  account_id: string
  severity: string
  open_issues: string
  ignored: string
  last_scanned: any
  created_at: any
}

interface EditConfigurationModalProps {
  isOpen: boolean
  onClose: () => void
  cloud: CloudData | null
  onUpdate: (updatedCloud: CloudData) => void
  onDelete?: (cloud: CloudData) => void
}

const EditConfigurationModal: React.FC<EditConfigurationModalProps> = ({
  isOpen,
  onClose,
  cloud,
  onUpdate,
  onDelete,
}) => {
  const {currentUser} = useAuth()
  const [name, setName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const purposeOptions = [
    {value: 'Production', label: 'Production'},
    {value: 'Development', label: 'Development'},
    {value: 'Mixed', label: 'Mixed'},
    {value: 'Staging', label: 'Staging'},
    {value: 'Testing', label: 'Testing'},
  ]

  useEffect(() => {
    if (cloud) {
      setName(cloud.name || '')
      setPurpose(cloud.purpose || 'Mixed')
    }
  }, [cloud])

  const handleSave = async () => {
    if (!cloud || !currentUser?.tenantId) return

    if (!name.trim()) {
      showErrorToast({message: 'Please enter a name for the cloud connection.'})
      return
    }

    setIsLoading(true)
    try {
      const updateData = {
        name: name.trim(),
        purpose: purpose.trim(),
      }

      await cloudService.updateCloud(currentUser.tenantId, cloud.id, updateData)

      const updatedCloud = {...cloud, ...updateData}
      onUpdate(updatedCloud)

      showSuccessToast({message: 'Cloud configuration updated successfully.'})
      onClose()
    } catch (error) {
      console.error('Error updating cloud:', error)
      showErrorToast({message: 'An error occurred while updating the cloud configuration.'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <CustomModal show={isOpen} onClose={onClose} title='' bodyStyle={{padding: '24px'}}>
      <div className='d-flex flex-column'>
        <p className='fw-bold mb-4 fs-1 text-gray-900 pb-0'>Edit your cloud configuration</p>{' '}
        <p className='text-gray-900 mb-4 fs-6'>
          Secureslate needs a little more information to give you results that are informed by context
          and tailored for your workspace.
        </p>
        <Input
          type='text'
          label='Name'
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='Enter cloud name'
          className='mb-4'
        />
        <div>
          <label className='form-label fw-semibold text-gray-900 mb-2'>Purpose</label>
          <select
            className='form-select fs-6 mb-4'
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            style={{
              borderRadius: '6px',
              padding: '12px 16px',
            }}
          >
            {purposeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className='d-flex justify-content-end mt-4'>
          <div className='d-flex gap-3'>
            <Button btnType='default' onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>

            <Button btnType='primary' onClick={handleSave} disabled={isLoading || !name.trim()}>
              {isLoading ? (
                <>
                  Saving... <span className='spinner-border spinner-border-sm align-middle'></span>
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  )
}

export default EditConfigurationModal
